import { useState } from "react";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useDroppable } from "@dnd-kit/core";
import { DocsComponentDemo } from "../DocsComponentDemo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  FileText,
  Linkedin,
  MapPin,
  Mail,
  GripVertical,
  Zap,
  Sparkles,
  Send,
  Video,
  Eye,
  ClipboardList,
  TrendingUp,
  CheckCircle2,
  Award,
  User,
  Briefcase,
  GraduationCap,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Calendar,
  StickyNote,
  Plus,
  Pin,
  Target,
  MessageSquare,
  Users,
  RefreshCw,
  Shield,
  Clock,
  Lightbulb,
  AlertTriangle,
  Flag,
  ClipboardCheck,
  Download,
  X,
  Brain,
  Heart,
  Globe,
  MessageCircleQuestion,
  ExternalLink,
  Info,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

// Enhanced Mock Data Interfaces
interface MockInterviewFeedback {
  scores: {
    overall: number;
    clarity: number;
    relevance: number;
    depthOfKnowledge: number;
  };
  summary: string;
  feedbackList: Array<{
    question: string;
    question_number: number;
    result: {
      highlight: string;
      downlight: string;
      question_summary: string;
    };
  }>;
}

interface MockWorkstyleResult {
  overall_fit_score: number;
  scores: {
    conscientiousness: number;
    communication: number;
    collaboration: number;
    adaptability: number;
    ownership: number;
    resilience: number;
  };
  completed_at: string;
  duration_sec: number;
}

interface MockParsedResume {
  ai_summary: string;
  hiring_recommendation: "Strong Hire" | "Hire" | "Maybe" | "No Hire";
  job_match_score: number;
  skills: string[];
  strengths: string[];
  concerns: string[];
  total_years_experience: number;
  highest_degree: string;
  career_trajectory: "Growing" | "Stable";
  languages: Array<{ language: string }>;
  experience: Array<{ title: string; company: string; start_date: string; end_date: string }>;
  education: Array<{ degree: string; field: string; institution: string; graduation_year: string }>;
}

interface MockPrepReport {
  candidate_snapshot: {
    resume_score: number;
    interview_score: number;
    workstyle_score: number;
    hiring_recommendation: string;
    total_experience: string;
  };
  key_insights: {
    strengths: string[];
    concerns: string[];
    red_flags: string[];
  };
  suggested_questions: Array<{
    question: string;
    category: "technical" | "behavioral" | "clarification" | "culture_fit";
    rationale: string;
  }>;
  preparation_tips: {
    topics_to_discuss: string[];
  };
}

interface MockNote {
  id: string;
  content: string;
  created_at: string;
  is_pinned: boolean;
}

interface DemoCandidate {
  id: string;
  name: string;
  email: string;
  location: string;
  resumeScore: number | null;
  interviewScore: number | null;
  interviewStatus: "not_sent" | "sent" | "invited" | "completed" | null;
  workstyleStatus: "not_sent" | "sent" | "completed" | null;
  rank?: number;
  interviewFeedback?: MockInterviewFeedback;
  workstyleResult?: MockWorkstyleResult;
  parsedResume?: MockParsedResume;
  prepReport?: MockPrepReport;
  notes?: MockNote[];
  stageKey?: string;
  appliedDate?: string;
}

interface DemoStage {
  id: string;
  key: string;
  name: string;
  isAuto?: boolean;
  autoLabel?: string;
}

// Mock Data
const mockInterviewFeedback: MockInterviewFeedback = {
  scores: { overall: 88, clarity: 85, relevance: 90, depthOfKnowledge: 88 },
  summary: "David demonstrated exceptional problem-solving abilities and clear communication skills. His technical depth is impressive, particularly in system design and architecture decisions. Shows strong leadership potential and cultural alignment.",
  feedbackList: [
    {
      question: "Tell me about a challenging project you led and how you overcame obstacles.",
      question_number: 1,
      result: {
        question_summary: "Discussed leading a migration project with tight deadlines.",
        highlight: "Provided specific examples with measurable outcomes. Demonstrated clear leadership and problem-solving approach.",
        downlight: "Could have elaborated more on team dynamics and collaboration aspects.",
      },
    },
    {
      question: "How do you approach system design for scalable applications?",
      question_number: 2,
      result: {
        question_summary: "Explained distributed systems approach with real examples.",
        highlight: "Excellent understanding of distributed systems and trade-offs. Mentioned specific technologies and patterns.",
        downlight: "Security considerations were briefly mentioned but could be expanded.",
      },
    },
    {
      question: "Describe your experience with agile methodologies.",
      question_number: 3,
      result: {
        question_summary: "Shared experience with Scrum and Kanban implementations.",
        highlight: "Strong practical experience with Scrum and Kanban. Good examples of sprint planning and retrospectives.",
        downlight: "Could discuss more about handling scope changes mid-sprint.",
      },
    },
  ],
};

const mockWorkstyleResult: MockWorkstyleResult = {
  overall_fit_score: 82,
  scores: { conscientiousness: 85, communication: 90, collaboration: 78, adaptability: 82, ownership: 88, resilience: 75 },
  completed_at: "2024-01-24T10:30:00Z",
  duration_sec: 720,
};

const mockParsedResume: MockParsedResume = {
  ai_summary: "Highly experienced full-stack developer with strong React and TypeScript expertise. Demonstrated ability to lead projects and mentor junior developers. Strong track record of improving system performance and delivering user-centric solutions.",
  hiring_recommendation: "Strong Hire",
  job_match_score: 91,
  skills: ["Go", "Python", "Kubernetes", "AWS", "System Design", "React", "TypeScript", "PostgreSQL"],
  strengths: ["8+ years of relevant experience at Google", "Deep expertise in distributed systems", "Strong leadership and mentoring skills", "Track record of performance optimization"],
  concerns: ["May be overqualified for mid-level position", "Salary expectations may exceed budget"],
  total_years_experience: 8,
  highest_degree: "Master's",
  career_trajectory: "Growing",
  languages: [{ language: "English" }, { language: "Mandarin" }],
  experience: [
    { title: "Staff Engineer", company: "Google", start_date: "2020-01", end_date: "Present" },
    { title: "Senior Engineer", company: "Stripe", start_date: "2017-03", end_date: "2019-12" },
    { title: "Software Engineer", company: "Dropbox", start_date: "2014-06", end_date: "2017-02" },
  ],
  education: [
    { degree: "M.S.", field: "Computer Science", institution: "MIT", graduation_year: "2014" },
    { degree: "B.S.", field: "Computer Science", institution: "UC Berkeley", graduation_year: "2012" },
  ],
};

const mockPrepReport: MockPrepReport = {
  candidate_snapshot: {
    resume_score: 92,
    interview_score: 90,
    workstyle_score: 86,
    hiring_recommendation: "Strong Hire",
    total_experience: "10 years",
  },
  key_insights: {
    strengths: [
      "Exceptional technical leadership experience at Atlassian",
      "Deep ML expertise with practical applications and publications",
      "Excellent interview scores across all dimensions",
      "Strong communication and presentation skills",
    ],
    concerns: [
      "Transition motivation from VP role needs clarification",
      "Experience with smaller team dynamics is unknown",
    ],
    red_flags: [],
  },
  suggested_questions: [
    {
      question: "What motivated your decision to explore opportunities outside Atlassian?",
      category: "behavioral",
      rationale: "Understand career motivations and ensure alignment with our growth stage",
    },
    {
      question: "How would you approach building ML infrastructure from the ground up vs. inheriting an existing system?",
      category: "technical",
      rationale: "Assess ability to adapt from enterprise to startup context",
    },
    {
      question: "Describe your ideal team structure and how you'd build it here.",
      category: "culture_fit",
      rationale: "Evaluate leadership style and team-building approach",
    },
    {
      question: "What aspects of our tech stack and product interest you most?",
      category: "clarification",
      rationale: "Gauge genuine interest and technical curiosity about our specific challenges",
    },
    {
      question: "How do you handle disagreements with product decisions that impact engineering timelines?",
      category: "behavioral",
      rationale: "Assess collaboration and communication style with cross-functional teams",
    },
  ],
  preparation_tips: {
    topics_to_discuss: [
      "Remote work policy and expectations",
      "Equity structure and growth trajectory",
      "Current team composition and hiring plans",
      "Product roadmap for next 12 months",
    ],
  },
};

const mockNotes: MockNote[] = [
  { id: "n1", content: "Strong communication skills during initial call. Very articulate.", created_at: "2024-01-20T14:30:00Z", is_pinned: true },
  { id: "n2", content: "Candidate asked about remote work policy and team size.", created_at: "2024-01-18T10:00:00Z", is_pinned: false },
];

const initialStages: DemoStage[] = [
  { id: "stage-new", key: "new", name: "New Applicants" },
  { id: "stage-shortlisted", key: "shortlisted", name: "Shortlisted", isAuto: true, autoLabel: "Auto" },
  { id: "stage-interview", key: "ai_invited", name: "AI Interview" },
  { id: "stage-ranked", key: "ranked", name: "Ranked", isAuto: true, autoLabel: "Auto-ranked" },
  { id: "stage-f2f", key: "f2f", name: "Face to Face" },
];

const initialCandidates: Record<string, DemoCandidate[]> = {
  "stage-new": [
    { id: "c1", name: "Alex Thompson", email: "alex.t@email.com", location: "New York, US", resumeScore: 78, interviewScore: null, interviewStatus: null, workstyleStatus: null, appliedDate: "2024-01-15" },
    { id: "c2", name: "Maria Garcia", email: "m.garcia@email.com", location: "Madrid, Spain", resumeScore: 82, interviewScore: null, interviewStatus: null, workstyleStatus: null, appliedDate: "2024-01-14" },
  ],
  "stage-shortlisted": [
    { id: "c3", name: "Sarah Chen", email: "sarah.c@email.com", location: "San Francisco, US", resumeScore: 91, interviewScore: null, interviewStatus: null, workstyleStatus: "not_sent", appliedDate: "2024-01-12", parsedResume: { ...mockParsedResume, job_match_score: 91 }, notes: mockNotes },
  ],
  "stage-interview": [
    { id: "c4", name: "Emily Johnson", email: "e.johnson@email.com", location: "Toronto, Canada", resumeScore: 85, interviewScore: null, interviewStatus: "invited", workstyleStatus: "sent", appliedDate: "2024-01-10" },
    { id: "c5", name: "Michael Park", email: "m.park@email.com", location: "Seoul, Korea", resumeScore: 88, interviewScore: null, interviewStatus: "not_sent", workstyleStatus: "not_sent", appliedDate: "2024-01-09" },
  ],
  "stage-ranked": [
    { id: "c6", name: "David Lee", email: "d.lee@email.com", location: "Singapore", resumeScore: 94, interviewScore: 88, interviewStatus: "completed", workstyleStatus: "completed", rank: 1, interviewFeedback: mockInterviewFeedback, workstyleResult: mockWorkstyleResult, parsedResume: mockParsedResume, notes: mockNotes, appliedDate: "2024-01-05" },
    { id: "c7", name: "Anna Schmidt", email: "a.schmidt@email.com", location: "Berlin, Germany", resumeScore: 89, interviewScore: 82, interviewStatus: "completed", workstyleStatus: "completed", rank: 2, interviewFeedback: { ...mockInterviewFeedback, scores: { overall: 82, clarity: 80, relevance: 85, depthOfKnowledge: 80 }, summary: "Anna showed solid technical skills and good problem-solving abilities. Her experience with European markets is valuable." }, workstyleResult: { ...mockWorkstyleResult, overall_fit_score: 78 }, appliedDate: "2024-01-06" },
  ],
  "stage-f2f": [
    { id: "c8", name: "Chris Brown", email: "c.brown@email.com", location: "Sydney, Australia", resumeScore: 92, interviewScore: 90, interviewStatus: "completed", workstyleStatus: "completed", rank: 1, interviewFeedback: { ...mockInterviewFeedback, scores: { overall: 90, clarity: 92, relevance: 88, depthOfKnowledge: 90 }, summary: "Chris is an outstanding candidate with excellent communication and deep technical expertise." }, workstyleResult: { ...mockWorkstyleResult, overall_fit_score: 86 }, parsedResume: mockParsedResume, prepReport: mockPrepReport, notes: mockNotes, appliedDate: "2024-01-03" },
  ],
};

// Utility Functions
function getScoreColor(score: number) {
  if (score >= 80) return "bg-green-500";
  if (score >= 60) return "bg-blue-500";
  if (score >= 40) return "bg-amber-500";
  return "bg-red-500";
}

function getScoreColorClass(score: number) {
  if (score >= 80) return "text-green-600";
  if (score >= 60) return "text-blue-600";
  if (score >= 40) return "text-amber-600";
  return "text-red-600";
}

function getRecommendationColor(recommendation: string) {
  switch (recommendation) {
    case "Strong Hire": return "bg-green-500/10 text-green-700 border-green-500/20";
    case "Hire": return "bg-emerald-500/10 text-emerald-700 border-emerald-500/20";
    case "Maybe": return "bg-yellow-500/10 text-yellow-700 border-yellow-500/20";
    case "No Hire": return "bg-red-500/10 text-red-700 border-red-500/20";
    default: return "bg-muted text-muted-foreground";
  }
}

function getOverallScoreConfig(score: number) {
  if (score >= 80) return { label: "Excellent Cultural Fit", colorClass: "text-green-600", bgClass: "from-green-500 to-emerald-500", borderClass: "border-green-200" };
  if (score >= 60) return { label: "Good Cultural Fit", colorClass: "text-blue-600", bgClass: "from-blue-500 to-cyan-500", borderClass: "border-blue-200" };
  if (score >= 40) return { label: "Moderate Fit", colorClass: "text-amber-600", bgClass: "from-amber-500 to-yellow-500", borderClass: "border-amber-200" };
  return { label: "Needs Consideration", colorClass: "text-red-600", bgClass: "from-red-500 to-orange-500", borderClass: "border-red-200" };
}

function getScoreLevel(score: number) {
  if (score >= 85) return { label: "Excellent", colorClass: "text-green-600", bgClass: "bg-green-500" };
  if (score >= 70) return { label: "Very Good", colorClass: "text-emerald-600", bgClass: "bg-emerald-500" };
  if (score >= 55) return { label: "Good", colorClass: "text-blue-600", bgClass: "bg-blue-500" };
  return { label: "Developing", colorClass: "text-amber-600", bgClass: "bg-amber-500" };
}

const DIMENSION_INFO: Record<string, { label: string; description: string; icon: typeof Zap }> = {
  conscientiousness: { label: "Structure Preferences", description: "Preference for defined processes versus flexibility and autonomy", icon: Target },
  communication: { label: "Communication", description: "Clear expression, aligns expectations, adapts to audience", icon: MessageSquare },
  collaboration: { label: "Collaboration", description: "Works well with others, supportive team player", icon: Users },
  adaptability: { label: "Adaptability", description: "Handles change well, learns quickly, flexible approach", icon: RefreshCw },
  ownership: { label: "Ownership", description: "Takes initiative, accountable for outcomes, self-driven", icon: Zap },
  resilience: { label: "Resilience", description: "Stays effective under pressure, persistent, bounces back", icon: Shield },
};

const categoryConfig: Record<string, { label: string; className: string }> = {
  technical: { label: "Technical", className: "bg-blue-100 text-blue-800 border-blue-200" },
  behavioral: { label: "Behavioral", className: "bg-purple-100 text-purple-800 border-purple-200" },
  clarification: { label: "Clarification", className: "bg-amber-100 text-amber-800 border-amber-200" },
  culture_fit: { label: "Culture Fit", className: "bg-green-100 text-green-800 border-green-200" },
};

// ========================================
// ENHANCED INTERVIEW RESULTS SHEET
// ========================================
function InterviewResultsSheet({ 
  open, 
  onOpenChange, 
  candidate 
}: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void; 
  candidate: DemoCandidate | null;
}) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  
  if (!candidate?.interviewFeedback) return null;
  
  const feedback = candidate.interviewFeedback;
  const scores = feedback.scores;
  const sortedFeedback = [...feedback.feedbackList].sort((a, b) => a.question_number - b.question_number);
  const totalQuestions = sortedFeedback.length;
  const currentQ = sortedFeedback[currentQuestion];
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="right" 
        className="w-full sm:max-w-full p-0 gap-0 [&>button]:hidden"
        style={{ maxWidth: "100vw" }}
      >
        {/* Header */}
        <SheetHeader className="px-6 py-4 border-b bg-background/95 backdrop-blur sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} className="h-9 w-9">
                <X className="h-5 w-5" />
              </Button>
              <div>
                <SheetTitle className="text-xl flex items-center gap-2">
                  <Video className="h-5 w-5 text-primary" />
                  Interview Results
                </SheetTitle>
                <p className="text-sm text-muted-foreground mt-0.5">{candidate.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>Completed Jan 25, 2024</span>
              </div>
              <Button variant="outline" size="sm" className="gap-2">
                <Download className="h-4 w-4" />
                Export PDF
              </Button>
            </div>
          </div>
        </SheetHeader>

        {/* Two Column Layout */}
        <div className="flex h-[calc(100vh-73px)]">
          {/* Left Column - Overview Panel */}
          <div className="w-[480px] flex-shrink-0 border-r bg-muted/30 overflow-y-auto">
            <div className="p-5 space-y-4">
              {/* Score Breakdown Card */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium">Score Breakdown</h3>
                    <div className={cn(
                      "px-3 py-1.5 rounded-lg flex items-center gap-2",
                      scores.overall >= 80 ? "bg-green-100" : scores.overall >= 60 ? "bg-blue-100" : "bg-amber-100"
                    )}>
                      <span className={cn("text-lg font-bold", getScoreColorClass(scores.overall))}>
                        {scores.overall}%
                      </span>
                      <span className={cn("text-xs font-medium", getScoreColorClass(scores.overall))}>
                        {scores.overall >= 80 ? "Excellent" : scores.overall >= 60 ? "Good" : "Average"}
                      </span>
                    </div>
                  </div>

                  {/* Radar Chart Placeholder */}
                  <div className="h-48 flex items-center justify-center bg-muted/50 rounded-lg mb-4">
                    <div className="text-center text-muted-foreground">
                      <TrendingUp className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Radar Chart</p>
                    </div>
                  </div>

                  {/* Score Labels */}
                  <TooltipProvider>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { label: "Communication", score: scores.clarity, tip: "How clearly the candidate expresses ideas" },
                        { label: "Relevance", score: scores.relevance, tip: "How well answers address the questions" },
                        { label: "Depth", score: scores.depthOfKnowledge, tip: "Technical depth and understanding" },
                      ].map((dim) => (
                        <div key={dim.label} className="text-center">
                          <p className="text-base font-bold text-primary">{dim.score}%</p>
                          <div className="flex items-center justify-center gap-1">
                            <p className="text-xs text-muted-foreground">{dim.label}</p>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-3 w-3 text-muted-foreground/60 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-[220px] text-center">
                                <p className="text-xs">{dim.tip}</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                      ))}
                    </div>
                  </TooltipProvider>
                </CardContent>
              </Card>

              {/* AI Summary */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <FileText className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium mb-1.5">AI Summary</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {feedback.summary}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Right Column - Question Detail Panel */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Question Navigator */}
            <div className="px-6 py-4 border-b bg-background flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium">Questions</span>
                <div className="flex items-center gap-1.5">
                  {sortedFeedback.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentQuestion(index)}
                      className={cn(
                        "w-8 h-8 rounded-full text-sm font-medium transition-all",
                        index === currentQuestion
                          ? "bg-primary text-primary-foreground scale-110"
                          : "bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <kbd className="px-2 py-1 rounded bg-muted font-mono">←</kbd>
                <kbd className="px-2 py-1 rounded bg-muted font-mono">→</kbd>
                <span>to navigate</span>
              </div>
            </div>

            {/* Question Content */}
            <ScrollArea className="flex-1">
              <div className="p-6">
                {currentQ && (
                  <div className="space-y-6">
                    {/* Question */}
                    <div className="p-4 rounded-lg bg-muted/50 border">
                      <p className="text-xs text-muted-foreground mb-1">Q{currentQ.question_number} of {totalQuestions}</p>
                      <p className="font-medium">{currentQ.question}</p>
                    </div>

                    {/* Video Placeholder */}
                    <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
                      <div className="text-center text-muted-foreground">
                        <Video className="h-16 w-16 mx-auto mb-3 opacity-50" />
                        <p className="text-sm font-medium">Video Response</p>
                        <p className="text-xs">(Demo placeholder)</p>
                      </div>
                    </div>

                    {/* AI Summary for Question */}
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Brain className="h-5 w-5 text-primary shrink-0" />
                          <div>
                            <h4 className="text-sm font-medium mb-1">AI Summary</h4>
                            <p className="text-sm text-muted-foreground">{currentQ.result.question_summary}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Strengths & Weaknesses */}
                    <div className="grid grid-cols-2 gap-4">
                      <Card className="border-green-200 bg-green-50/50">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                            <h4 className="text-sm font-medium text-green-800">Strengths</h4>
                          </div>
                          <p className="text-sm text-green-700">{currentQ.result.highlight}</p>
                        </CardContent>
                      </Card>
                      <Card className="border-amber-200 bg-amber-50/50">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                            <h4 className="text-sm font-medium text-amber-800">Areas to Improve</h4>
                          </div>
                          <p className="text-sm text-amber-700">{currentQ.result.downlight}</p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Navigation Footer */}
            <div className="px-6 py-4 border-t bg-background flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => setCurrentQuestion((prev) => prev - 1)}
                disabled={currentQuestion === 0}
                className="gap-2"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous Question
              </Button>
              <Badge variant="secondary" className="text-sm">
                {currentQuestion + 1} / {totalQuestions}
              </Badge>
              <Button
                variant="outline"
                onClick={() => setCurrentQuestion((prev) => prev + 1)}
                disabled={currentQuestion === totalQuestions - 1}
                className="gap-2"
              >
                Next Question
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ========================================
// ENHANCED WORK STYLE RESULTS DIALOG
// ========================================
function WorkStyleResultsSheet({ 
  open, 
  onOpenChange, 
  candidate 
}: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void; 
  candidate: DemoCandidate | null;
}) {
  if (!candidate?.workstyleResult) return null;
  
  const result = candidate.workstyleResult;
  const scores = result.scores;
  const config = getOverallScoreConfig(result.overall_fit_score);
  
  const dimensions = Object.keys(DIMENSION_INFO) as Array<keyof typeof DIMENSION_INFO>;
  
  // Generate insights
  const sortedDims = Object.entries(scores).sort(([, a], [, b]) => b - a);
  const strengths = sortedDims.filter(([_, s]) => s >= 75).slice(0, 3).map(([d]) => DIMENSION_INFO[d]?.label.toLowerCase());
  const explore = sortedDims.filter(([_, s]) => s < 60).slice(0, 2).map(([d]) => DIMENSION_INFO[d]?.label.toLowerCase());
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Work Style Results
          </SheetTitle>
          <SheetDescription>{candidate.name} • Behavioral assessment</SheetDescription>
        </SheetHeader>
        
        <div className="space-y-6 mt-6">
          {/* Hero Score Section */}
          <div className={cn("relative p-6 rounded-xl border-2 bg-gradient-to-br from-muted/50 to-muted", config.borderClass)}>
            <div className="text-center">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Overall Fit Score</p>
              <div className={cn("text-5xl font-bold bg-gradient-to-r bg-clip-text text-transparent", config.bgClass)}>
                {result.overall_fit_score}%
              </div>
              <div className="w-full h-3 bg-muted rounded-full overflow-hidden my-3">
                <div className={cn("h-full rounded-full bg-gradient-to-r transition-all duration-1000", config.bgClass)} style={{ width: `${result.overall_fit_score}%` }} />
              </div>
              <p className={cn("text-sm font-semibold", config.colorClass)}>{config.label}</p>
            </div>
          </div>

          <Separator />

          {/* Dimension Breakdown */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <span className="text-base">📊</span>
              Dimension Breakdown
            </h4>
            <TooltipProvider delayDuration={200}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {dimensions.map((key) => {
                  const info = DIMENSION_INFO[key];
                  const score = scores[key as keyof typeof scores] || 0;
                  const level = getScoreLevel(score);
                  const Icon = info.icon;
                  
                  return (
                    <Tooltip key={key}>
                      <TooltipTrigger asChild>
                        <div className="p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-default">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="p-1.5 rounded-md bg-muted">
                              <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                            </div>
                            <span className="text-sm font-medium flex-1">{info.label}</span>
                            <span className={cn("text-sm font-bold", level.colorClass)}>{score}%</span>
                          </div>
                          <div className="h-2 w-full bg-muted rounded-full overflow-hidden mb-2">
                            <div className={cn("h-full transition-all duration-500", level.bgClass)} style={{ width: `${score}%` }} />
                          </div>
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-muted-foreground line-clamp-1">{info.description}</p>
                            <Badge variant="outline" className={cn("text-[10px] shrink-0 ml-2", level.colorClass)}>
                              {level.label}
                            </Badge>
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <p className="font-medium">{info.label}</p>
                        <p className="text-sm text-muted-foreground">{info.description}</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </TooltipProvider>
          </div>

          <Separator />

          {/* Key Insights */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-amber-500" />
              Key Insights
            </h4>
            <div className="space-y-2">
              {strengths.length > 0 && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-green-50 border border-green-200">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Strengths</p>
                    <p className="text-sm text-green-700">Strong in {strengths.join(", ")}.</p>
                  </div>
                </div>
              )}
              {explore.length > 0 && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <Target className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Areas to Explore</p>
                    <p className="text-sm text-amber-700">Consider discussing {explore.join(" and ")} during the interview.</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Assessment Metadata */}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              <span>Completed Jan 24, 2024</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              <span>Duration: {Math.floor(result.duration_sec / 60)} min</span>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ========================================
// ENHANCED CANDIDATE DETAILS DRAWER WITH TABS
// ========================================
function CandidateDetailsDrawer({ 
  open, 
  onOpenChange, 
  candidate,
  currentStageName = "Unknown",
}: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void; 
  candidate: DemoCandidate | null;
  currentStageName?: string;
}) {
  const [activeTab, setActiveTab] = useState("overview");
  const [isExperienceOpen, setIsExperienceOpen] = useState(false);
  const [isEducationOpen, setIsEducationOpen] = useState(false);
  
  if (!candidate) return null;
  
  const notes = candidate.notes || [];
  const parsedResume = candidate.parsedResume;
  const workstyleResult = candidate.workstyleResult;
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-xl overflow-hidden flex flex-col p-0">
        {/* Header */}
        <div className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <SheetTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {candidate.name}
              </SheetTitle>
              <p className="text-sm text-muted-foreground">{candidate.email}</p>
            </div>
            <Button variant="outline" size="sm" className="gap-2">
              <ExternalLink className="h-4 w-4" />
              Share
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <div className="px-6 border-b">
            <TabsList className="w-full justify-start h-auto p-0 bg-transparent">
              <TabsTrigger value="overview" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-4 py-3">
                Overview
              </TabsTrigger>
              <TabsTrigger value="notes" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-4 py-3">
                <StickyNote className="h-4 w-4 mr-1" />
                Notes
                {notes.length > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">{notes.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="ai-insights" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-4 py-3">
                <Sparkles className="h-4 w-4 mr-1" />
                AI
              </TabsTrigger>
              <TabsTrigger value="workstyle" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-4 py-3">
                <TrendingUp className="h-4 w-4 mr-1" />
                Work Style
              </TabsTrigger>
            </TabsList>
          </div>

          <ScrollArea className="flex-1">
            {/* Overview Tab */}
            <TabsContent value="overview" className="m-0 p-6 space-y-6">
              {/* Contact Info */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground">Contact</h4>
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <a href={`mailto:${candidate.email}`} className="hover:underline">{candidate.email}</a>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{candidate.location}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>Applied {candidate.appliedDate || "January 15, 2024"}</span>
                </div>
              </div>

              <Separator />

              {/* Current Stage */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground">Current Stage</h4>
                <Badge variant="secondary" className="text-sm">{currentStageName}</Badge>
              </div>

              <Separator />

              {/* Documents */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground">Documents</h4>
                <div className="p-3 rounded-lg border bg-muted/30 space-y-2">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">Resume</span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">resume-{candidate.name.toLowerCase().replace(' ', '-')}.pdf</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1">
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="w-full gap-2">
                  <Linkedin className="h-4 w-4 text-blue-600" />
                  LinkedIn Profile
                </Button>
              </div>

              <Separator />

              {/* AI Analysis */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground">AI Analysis</h4>
                <div className="grid grid-cols-2 gap-3">
                  {candidate.resumeScore && (
                    <div className="p-3 rounded-lg border bg-muted/30 text-center">
                      <div className={cn("text-2xl font-bold", getScoreColorClass(candidate.resumeScore))}>
                        {candidate.resumeScore}%
                      </div>
                      <div className="text-xs text-muted-foreground">Resume Score</div>
                    </div>
                  )}
                  {candidate.interviewScore && (
                    <div className="p-3 rounded-lg border bg-muted/30 text-center">
                      <div className={cn("text-2xl font-bold", getScoreColorClass(candidate.interviewScore))}>
                        {candidate.interviewScore}%
                      </div>
                      <div className="text-xs text-muted-foreground">Interview Score</div>
                    </div>
                  )}
                </div>
                {candidate.interviewStatus === "completed" && (
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Scored
                    </Badge>
                    {candidate.rank && (
                      <Badge variant="outline" className="text-xs">Rank: #{candidate.rank}</Badge>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes" className="m-0 p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-muted-foreground">Notes ({notes.length})</h4>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Note
                </Button>
              </div>

              {notes.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="rounded-full bg-muted p-4 mb-4">
                    <StickyNote className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-sm font-medium mb-1">No notes yet</h3>
                  <p className="text-xs text-muted-foreground mb-4 max-w-[200px]">
                    Add notes to keep track of important information
                  </p>
                  <Button size="sm" variant="outline">
                    <Plus className="h-4 w-4 mr-1" />
                    Add your first note
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {notes.map((note) => (
                    <Card key={note.id}>
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm">{note.content}</p>
                          {note.is_pinned && <Pin className="h-4 w-4 text-primary shrink-0" />}
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">You • {new Date(note.created_at).toLocaleDateString()}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* AI Insights Tab */}
            <TabsContent value="ai-insights" className="m-0 p-6 space-y-4">
              {parsedResume ? (
                <>
                  {/* AI Summary & Recommendation */}
                  <div className="p-4 rounded-lg border bg-muted/30 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium">AI Analysis</span>
                      </div>
                      <Badge className={getRecommendationColor(parsedResume.hiring_recommendation)}>
                        {parsedResume.hiring_recommendation}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{parsedResume.ai_summary}</p>
                    
                    {/* Match Score */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Job Match:</span>
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full" style={{ width: `${parsedResume.job_match_score}%` }} />
                      </div>
                      <span className="text-sm font-medium">{parsedResume.job_match_score}%</span>
                    </div>
                  </div>

                  {/* Skills */}
                  <div className="space-y-2">
                    <h5 className="text-sm font-medium flex items-center gap-2">
                      <Award className="h-4 w-4 text-muted-foreground" />
                      Skills
                    </h5>
                    <div className="flex flex-wrap gap-1">
                      {parsedResume.skills.slice(0, 12).map((skill, index) => (
                        <Badge key={index} variant="outline" className="text-xs">{skill}</Badge>
                      ))}
                    </div>
                  </div>

                  {/* Strengths & Concerns */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <h5 className="text-xs font-medium text-green-700">Strengths</h5>
                      <ul className="space-y-1">
                        {parsedResume.strengths.slice(0, 3).map((strength, index) => (
                          <li key={index} className="text-xs text-muted-foreground flex items-start gap-1">
                            <span className="text-green-600">+</span>
                            {strength}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <h5 className="text-xs font-medium text-amber-700">Concerns</h5>
                      <ul className="space-y-1">
                        {parsedResume.concerns.slice(0, 3).map((concern, index) => (
                          <li key={index} className="text-xs text-muted-foreground flex items-start gap-1">
                            <span className="text-amber-600">!</span>
                            {concern}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <Separator />

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Experience:</span>
                      <span className="font-medium">{parsedResume.total_years_experience} years</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <GraduationCap className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Degree:</span>
                      <span className="font-medium">{parsedResume.highest_degree}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Trajectory:</span>
                      <span className="font-medium">{parsedResume.career_trajectory}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Languages:</span>
                      <span className="font-medium">{parsedResume.languages.map(l => l.language).join(", ")}</span>
                    </div>
                  </div>

                  <Separator />

                  {/* Experience (Collapsible) */}
                  <Collapsible open={isExperienceOpen} onOpenChange={setIsExperienceOpen}>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" className="w-full justify-between p-2 h-auto">
                        <span className="flex items-center gap-2 text-sm">
                          <Briefcase className="h-4 w-4" />
                          Experience ({parsedResume.experience.length} roles)
                        </span>
                        {isExperienceOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-3 pt-2">
                      {parsedResume.experience.map((exp, index) => (
                        <div key={index} className="border-l-2 border-muted pl-3 space-y-1">
                          <p className="text-sm font-medium">{exp.title}</p>
                          <p className="text-xs text-muted-foreground">{exp.company}</p>
                          <p className="text-xs text-muted-foreground">{exp.start_date} - {exp.end_date}</p>
                        </div>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Education (Collapsible) */}
                  <Collapsible open={isEducationOpen} onOpenChange={setIsEducationOpen}>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" className="w-full justify-between p-2 h-auto">
                        <span className="flex items-center gap-2 text-sm">
                          <GraduationCap className="h-4 w-4" />
                          Education ({parsedResume.education.length})
                        </span>
                        {isEducationOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-3 pt-2">
                      {parsedResume.education.map((edu, index) => (
                        <div key={index} className="border-l-2 border-muted pl-3 space-y-1">
                          <p className="text-sm font-medium">{edu.degree} in {edu.field}</p>
                          <p className="text-xs text-muted-foreground">{edu.institution}</p>
                          <p className="text-xs text-muted-foreground">{edu.graduation_year}</p>
                        </div>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                </>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Sparkles className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-sm">Resume not yet analyzed</p>
                </div>
              )}
            </TabsContent>

            {/* Work Style Tab */}
            <TabsContent value="workstyle" className="m-0 p-6 space-y-4">
              {workstyleResult ? (
                <>
                  {/* Hero Score */}
                  <div className={cn("relative p-6 rounded-xl border-2 bg-gradient-to-br from-muted/50 to-muted", getOverallScoreConfig(workstyleResult.overall_fit_score).borderClass)}>
                    <div className="text-center">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Overall Fit Score</p>
                      <div className={cn("text-5xl font-bold bg-gradient-to-r bg-clip-text text-transparent", getOverallScoreConfig(workstyleResult.overall_fit_score).bgClass)}>
                        {workstyleResult.overall_fit_score}%
                      </div>
                      <div className="w-full h-3 bg-muted rounded-full overflow-hidden my-3">
                        <div className={cn("h-full rounded-full bg-gradient-to-r", getOverallScoreConfig(workstyleResult.overall_fit_score).bgClass)} style={{ width: `${workstyleResult.overall_fit_score}%` }} />
                      </div>
                      <p className={cn("text-sm font-semibold", getOverallScoreConfig(workstyleResult.overall_fit_score).colorClass)}>
                        {getOverallScoreConfig(workstyleResult.overall_fit_score).label}
                      </p>
                    </div>
                  </div>

                  <Separator />

                  {/* Dimension Breakdown */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold">Dimension Breakdown</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {Object.entries(workstyleResult.scores).map(([key, score]) => {
                        const info = DIMENSION_INFO[key];
                        const level = getScoreLevel(score);
                        const Icon = info?.icon || Target;
                        
                        return (
                          <div key={key} className="p-3 rounded-lg border bg-card">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="p-1.5 rounded-md bg-muted">
                                <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                              </div>
                              <span className="text-sm font-medium flex-1">{info?.label}</span>
                              <span className={cn("text-sm font-bold", level.colorClass)}>{score}%</span>
                            </div>
                            <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                              <div className={cn("h-full", level.bgClass)} style={{ width: `${score}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-sm">Work style assessment not completed</p>
                </div>
              )}
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

// ========================================
// PREPARE INTERVIEW DRAWER
// ========================================
function PrepareInterviewDrawer({ 
  open, 
  onOpenChange, 
  candidate 
}: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void; 
  candidate: DemoCandidate | null;
}) {
  const [notes, setNotes] = useState("");
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  
  if (!candidate?.prepReport) return null;
  
  const report = candidate.prepReport;

  // Simulate auto-save
  const handleNotesChange = (value: string) => {
    setNotes(value);
    setSaveStatus('saving');
    setTimeout(() => {
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }, 1000);
  };
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-hidden flex flex-col">
        <SheetHeader className="shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-primary/10">
                <ClipboardCheck className="h-5 w-5 text-primary" />
              </div>
              <div>
                <SheetTitle className="text-lg">Interview Preparation</SheetTitle>
                <SheetDescription className="text-sm">
                  {candidate.name} for Senior Frontend Engineer
                </SheetDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="gap-1.5">
                <Download className="h-4 w-4" />
                Export
              </Button>
              <Button variant="ghost" size="sm" className="gap-1.5">
                <RefreshCw className="h-4 w-4" />
                Regenerate
              </Button>
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1 mt-4 -mr-4 pr-4">
          <div className="space-y-6 pb-6">
            {/* Candidate Snapshot */}
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                <User className="h-4 w-4" />
                Candidate Snapshot
              </h3>
              <div className="grid grid-cols-3 gap-3">
                <div className={cn("p-3 rounded-lg border", report.candidate_snapshot.resume_score >= 80 ? "text-green-700 bg-green-50 border-green-200" : "text-amber-700 bg-amber-50 border-amber-200")}>
                  <div className="flex items-center gap-2 mb-1">
                    <Briefcase className="h-4 w-4" />
                    <span className="text-xs font-medium">Resume</span>
                  </div>
                  <div className="text-2xl font-bold">{report.candidate_snapshot.resume_score}%</div>
                </div>
                <div className={cn("p-3 rounded-lg border", report.candidate_snapshot.interview_score >= 80 ? "text-green-700 bg-green-50 border-green-200" : "text-amber-700 bg-amber-50 border-amber-200")}>
                  <div className="flex items-center gap-2 mb-1">
                    <Brain className="h-4 w-4" />
                    <span className="text-xs font-medium">Interview</span>
                  </div>
                  <div className="text-2xl font-bold">{report.candidate_snapshot.interview_score}%</div>
                </div>
                <div className={cn("p-3 rounded-lg border", report.candidate_snapshot.workstyle_score >= 80 ? "text-green-700 bg-green-50 border-green-200" : "text-amber-700 bg-amber-50 border-amber-200")}>
                  <div className="flex items-center gap-2 mb-1">
                    <Heart className="h-4 w-4" />
                    <span className="text-xs font-medium">Work Style</span>
                  </div>
                  <div className="text-2xl font-bold">{report.candidate_snapshot.workstyle_score}%</div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Badge variant="secondary" className="text-xs">{report.candidate_snapshot.hiring_recommendation}</Badge>
                <Badge variant="outline" className="text-xs">{report.candidate_snapshot.total_experience}</Badge>
              </div>
            </div>

            <Separator />

            {/* Key Insights */}
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                <Lightbulb className="h-4 w-4" />
                Key Insights
              </h3>
              <div className="space-y-3">
                {/* Strengths */}
                {report.key_insights.strengths.length > 0 && (
                  <Card className="border-green-200 bg-green-50/50">
                    <CardHeader className="pb-2 pt-3 px-4">
                      <CardTitle className="text-sm font-medium text-green-800 flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4" />
                        Strengths to Leverage
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 pb-3">
                      <ul className="space-y-1.5">
                        {report.key_insights.strengths.map((strength, i) => (
                          <li key={i} className="text-sm text-green-900 flex items-start gap-2">
                            <span className="text-green-600 mt-1">•</span>
                            {strength}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Concerns */}
                {report.key_insights.concerns.length > 0 && (
                  <Card className="border-amber-200 bg-amber-50/50">
                    <CardHeader className="pb-2 pt-3 px-4">
                      <CardTitle className="text-sm font-medium text-amber-800 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Areas to Explore
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 pb-3">
                      <ul className="space-y-1.5">
                        {report.key_insights.concerns.map((concern, i) => (
                          <li key={i} className="text-sm text-amber-900 flex items-start gap-2">
                            <span className="text-amber-600 mt-1">•</span>
                            {concern}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Red Flags */}
                {report.key_insights.red_flags.length > 0 && (
                  <Card className="border-red-200 bg-red-50/50">
                    <CardHeader className="pb-2 pt-3 px-4">
                      <CardTitle className="text-sm font-medium text-red-800 flex items-center gap-2">
                        <Flag className="h-4 w-4" />
                        Potential Red Flags
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 pb-3">
                      <ul className="space-y-1.5">
                        {report.key_insights.red_flags.map((flag, i) => (
                          <li key={i} className="text-sm text-red-900 flex items-start gap-2">
                            <span className="text-red-600 mt-1">•</span>
                            {flag}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>

            <Separator />

            {/* Suggested Questions */}
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                <MessageCircleQuestion className="h-4 w-4" />
                Suggested Questions ({report.suggested_questions.length})
              </h3>
              <div className="space-y-3">
                {report.suggested_questions.map((q, i) => {
                  const config = categoryConfig[q.category] || categoryConfig.clarification;
                  return (
                    <Card key={i} className="border-l-4 border-l-primary/50">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-sm font-semibold">
                              {i + 1}
                            </span>
                            <Badge variant="outline" className={`text-xs ${config.className}`}>
                              {config.label}
                            </Badge>
                          </div>
                        </div>
                        <p className="font-medium text-foreground mb-2">{q.question}</p>
                        <p className="text-sm text-muted-foreground italic">
                          <span className="font-medium not-italic">Why ask this: </span>
                          {q.rationale}
                        </p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            <Separator />

            {/* Preparation Tips */}
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                <Target className="h-4 w-4" />
                Preparation Tips
              </h3>
              <Card className="border-blue-200 bg-blue-50/50">
                <CardHeader className="pb-2 pt-3 px-4">
                  <CardTitle className="text-sm font-medium text-blue-800">Topics to Discuss</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-3">
                  <ul className="space-y-1.5">
                    {report.preparation_tips.topics_to_discuss.map((topic, i) => (
                      <li key={i} className="text-sm text-blue-900 flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        {topic}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            <Separator />

            {/* My Notes */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                  <StickyNote className="h-4 w-4" />
                  My Notes
                </h3>
                {saveStatus !== 'idle' && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    {saveStatus === 'saving' && "Saving..."}
                    {saveStatus === 'saved' && (
                      <>
                        <CheckCircle2 className="h-3 w-3 text-green-600" />
                        Auto-saved
                      </>
                    )}
                  </span>
                )}
              </div>
              <Textarea
                placeholder="Add your notes for this interview..."
                value={notes}
                onChange={(e) => handleNotesChange(e.target.value)}
                className="min-h-[100px] resize-none"
              />
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

// ========================================
// CANDIDATE CARD COMPONENTS
// ========================================
function SortableCandidateCard({ 
  candidate,
  stageKey,
  onSendInterview,
  onSendWorkstyle,
  onViewInterviewResults,
  onViewWorkstyleResults,
  onViewDetails,
  onPrepareInterview,
}: { 
  candidate: DemoCandidate;
  stageKey: string;
  onSendInterview: (candidate: DemoCandidate) => void;
  onSendWorkstyle: (candidate: DemoCandidate) => void;
  onViewInterviewResults: (candidate: DemoCandidate) => void;
  onViewWorkstyleResults: (candidate: DemoCandidate) => void;
  onViewDetails: (candidate: DemoCandidate) => void;
  onPrepareInterview: (candidate: DemoCandidate) => void;
}) {
  const isInRankedStage = stageKey === "ranked";
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: candidate.id,
    disabled: isInRankedStage,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn("touch-none", isDragging && "opacity-50")}
    >
      <CandidateCardContent 
        candidate={candidate}
        stageKey={stageKey}
        dragHandleProps={isInRankedStage ? undefined : { ...attributes, ...listeners }}
        onSendInterview={onSendInterview}
        onSendWorkstyle={onSendWorkstyle}
        onViewInterviewResults={onViewInterviewResults}
        onViewWorkstyleResults={onViewWorkstyleResults}
        onViewDetails={onViewDetails}
        onPrepareInterview={onPrepareInterview}
      />
    </div>
  );
}

function CandidateCardContent({ 
  candidate,
  stageKey,
  dragHandleProps,
  isOverlay = false,
  onSendInterview,
  onSendWorkstyle,
  onViewInterviewResults,
  onViewWorkstyleResults,
  onViewDetails,
  onPrepareInterview,
}: { 
  candidate: DemoCandidate;
  stageKey?: string;
  dragHandleProps?: Record<string, unknown>;
  isOverlay?: boolean;
  onSendInterview?: (candidate: DemoCandidate) => void;
  onSendWorkstyle?: (candidate: DemoCandidate) => void;
  onViewInterviewResults?: (candidate: DemoCandidate) => void;
  onViewWorkstyleResults?: (candidate: DemoCandidate) => void;
  onViewDetails?: (candidate: DemoCandidate) => void;
  onPrepareInterview?: (candidate: DemoCandidate) => void;
}) {
  const isRankedOrF2F = stageKey === "ranked" || stageKey === "f2f";
  
  return (
    <Card className={cn("p-3 space-y-2 cursor-default", isOverlay && "shadow-lg rotate-2 scale-105")}>
      <div className="flex items-start gap-2">
        {dragHandleProps && (
          <div {...dragHandleProps} className="mt-1 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground">
            <GripVertical className="h-4 w-4" />
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {candidate.rank && (
              <Badge 
                variant="outline" 
                className={cn(
                  "text-xs font-bold shrink-0",
                  candidate.rank === 1 && "bg-yellow-100 text-yellow-800 border-yellow-300",
                  candidate.rank === 2 && "bg-gray-100 text-gray-700 border-gray-300",
                  candidate.rank === 3 && "bg-orange-100 text-orange-800 border-orange-300"
                )}
              >
                #{candidate.rank}
              </Badge>
            )}
            <div className="font-medium text-sm truncate">{candidate.name}</div>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Mail className="h-3 w-3 shrink-0" />
            <span className="truncate">{candidate.email}</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">{candidate.location}</span>
          </div>
        </div>
      </div>

      {/* Score Badges */}
      <div className="flex flex-wrap gap-1.5">
        {candidate.resumeScore && (
          <Badge variant="outline" className={cn("text-white text-xs", getScoreColor(candidate.resumeScore))}>
            Resume: {candidate.resumeScore}%
          </Badge>
        )}
        {candidate.interviewScore && (
          <Badge variant="outline" className={cn("text-white text-xs", getScoreColor(candidate.interviewScore))}>
            Interview: {candidate.interviewScore}%
          </Badge>
        )}
      </div>

      {/* Interview Status Badge */}
      {candidate.interviewStatus === "invited" && (
        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
          Interview Invited
        </Badge>
      )}
      {candidate.interviewStatus === "completed" && (
        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Interview Completed
        </Badge>
      )}

      {/* Stage-specific Action Buttons */}
      <div className="space-y-1.5 pt-1 border-t">
        {/* AI Interview stage - Send/Resend Interview button */}
        {stageKey === "ai_invited" && (
          <>
            {(!candidate.interviewStatus || candidate.interviewStatus === "not_sent") && (
              <Button variant="default" size="sm" className="w-full h-7 text-xs" onClick={() => onSendInterview?.(candidate)}>
                <Send className="h-3 w-3 mr-1" />
                Send AI Interview
              </Button>
            )}
            {candidate.interviewStatus === "invited" && (
              <Button variant="outline" size="sm" className="w-full h-7 text-xs" onClick={() => onSendInterview?.(candidate)}>
                <Send className="h-3 w-3 mr-1" />
                Resend AI Interview
              </Button>
            )}
          </>
        )}

        {/* Ranked/F2F - View Interview Results */}
        {isRankedOrF2F && candidate.interviewFeedback && (
          <Button variant="default" size="sm" className="w-full h-7 text-xs" onClick={() => onViewInterviewResults?.(candidate)}>
            <Video className="h-3 w-3 mr-1" />
            View Interview Results
          </Button>
        )}

        {/* F2F - Prepare Interview */}
        {stageKey === "f2f" && candidate.prepReport && (
          <Button variant="outline" size="sm" className="w-full h-7 text-xs" onClick={() => onPrepareInterview?.(candidate)}>
            <ClipboardCheck className="h-3 w-3 mr-1" />
            Prepare Interview
          </Button>
        )}

        {/* Work Style Test - all stages except new */}
        {stageKey !== "new" && (
          <>
            {(!candidate.workstyleStatus || candidate.workstyleStatus === "not_sent") && (
              <Button variant="outline" size="sm" className="w-full h-7 text-xs" onClick={() => onSendWorkstyle?.(candidate)}>
                <ClipboardList className="h-3 w-3 mr-1" />
                Send Work Style Test
              </Button>
            )}
            {candidate.workstyleStatus === "sent" && (
              <Button variant="outline" size="sm" className="w-full h-7 text-xs" onClick={() => onSendWorkstyle?.(candidate)}>
                <ClipboardList className="h-3 w-3 mr-1" />
                Resend Work Style Test
              </Button>
            )}
            {candidate.workstyleStatus === "completed" && (
              <Button variant="default" size="sm" className="w-full h-7 text-xs" onClick={() => onViewWorkstyleResults?.(candidate)}>
                <TrendingUp className="h-3 w-3 mr-1" />
                View Work Style Result
              </Button>
            )}
          </>
        )}

        {/* Quick Actions Row */}
        <div className="flex gap-1.5">
          <Button variant="outline" size="sm" className="flex-1 h-7 text-xs gap-1">
            <FileText className="h-3 w-3" />
            Resume
          </Button>
          <Button variant="outline" size="sm" className="flex-1 h-7 text-xs gap-1">
            <Linkedin className="h-3 w-3 text-blue-600" />
            LinkedIn
          </Button>
        </div>

        {/* View Details */}
        <Button variant="outline" size="sm" className="w-full h-7 text-xs" onClick={() => onViewDetails?.(candidate)}>
          <Eye className="h-3 w-3 mr-1" />
          View Details
        </Button>
      </div>
    </Card>
  );
}

// ========================================
// STAGE COLUMN
// ========================================
function StageColumn({ 
  stage, 
  candidates,
  onSendInterview,
  onSendWorkstyle,
  onViewInterviewResults,
  onViewWorkstyleResults,
  onViewDetails,
  onPrepareInterview,
}: { 
  stage: DemoStage; 
  candidates: DemoCandidate[];
  onSendInterview: (candidate: DemoCandidate) => void;
  onSendWorkstyle: (candidate: DemoCandidate) => void;
  onViewInterviewResults: (candidate: DemoCandidate) => void;
  onViewWorkstyleResults: (candidate: DemoCandidate) => void;
  onViewDetails: (candidate: DemoCandidate) => void;
  onPrepareInterview: (candidate: DemoCandidate) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex flex-col w-72 shrink-0 rounded-lg border transition-colors h-[520px]",
        isOver ? "border-primary bg-primary/5" : "border-border bg-muted/30"
      )}
    >
      {/* Stage Header */}
      <div className="shrink-0 p-3 border-b bg-card rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-sm">{stage.name}</h3>
            <Badge variant="secondary" className="text-xs">{candidates.length}</Badge>
          </div>
          <div className="flex items-center gap-1">
            {stage.isAuto && stage.key === "ranked" && (
              <div className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                <Sparkles className="h-3 w-3" />
                <span>{stage.autoLabel}</span>
              </div>
            )}
            {stage.isAuto && stage.key === "shortlisted" && (
              <div className="flex items-center gap-1 text-xs text-amber-600">
                <Zap className="h-3 w-3" />
                <span>{stage.autoLabel}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Candidates List */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-2">
          {candidates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <p className="text-sm">No candidates</p>
              <p className="text-xs">Drag candidates here</p>
            </div>
          ) : (
            <SortableContext items={candidates.map((c) => c.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {candidates.map((candidate) => (
                  <SortableCandidateCard 
                    key={candidate.id} 
                    candidate={candidate}
                    stageKey={stage.key}
                    onSendInterview={onSendInterview}
                    onSendWorkstyle={onSendWorkstyle}
                    onViewInterviewResults={onViewInterviewResults}
                    onViewWorkstyleResults={onViewWorkstyleResults}
                    onViewDetails={onViewDetails}
                    onPrepareInterview={onPrepareInterview}
                  />
                ))}
              </div>
            </SortableContext>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

// ========================================
// MAIN COMPONENT
// ========================================
export function DemoPipelineBoard() {
  const [candidatesByStage, setCandidatesByStage] = useState(initialCandidates);
  const [activeCandidate, setActiveCandidate] = useState<DemoCandidate | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<DemoCandidate | null>(null);
  const [selectedStageName, setSelectedStageName] = useState<string>("Unknown");
  const [interviewResultsOpen, setInterviewResultsOpen] = useState(false);
  const [workstyleResultsOpen, setWorkstyleResultsOpen] = useState(false);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [prepareInterviewOpen, setPrepareInterviewOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const findStageKey = (candidateId: string): string | null => {
    for (const stageId of Object.keys(candidatesByStage)) {
      if (candidatesByStage[stageId].some((c) => c.id === candidateId)) {
        const stage = initialStages.find((s) => s.id === stageId);
        return stage?.key || null;
      }
    }
    return null;
  };

  const findStageName = (candidateId: string): string => {
    for (const stageId of Object.keys(candidatesByStage)) {
      if (candidatesByStage[stageId].some((c) => c.id === candidateId)) {
        const stage = initialStages.find((s) => s.id === stageId);
        return stage?.name || "Unknown";
      }
    }
    return "Unknown";
  };

  const handleSendInterview = (candidate: DemoCandidate) => {
    setCandidatesByStage((prev) => {
      const newState = { ...prev };
      for (const stageId of Object.keys(newState)) {
        newState[stageId] = newState[stageId].map((c) =>
          c.id === candidate.id ? { ...c, interviewStatus: "invited" as const } : c
        );
      }
      return newState;
    });
    toast.success(`AI Interview invitation sent to ${candidate.name}`);
  };

  const handleSendWorkstyle = (candidate: DemoCandidate) => {
    setCandidatesByStage((prev) => {
      const newState = { ...prev };
      for (const stageId of Object.keys(newState)) {
        newState[stageId] = newState[stageId].map((c) =>
          c.id === candidate.id ? { ...c, workstyleStatus: "sent" as const } : c
        );
      }
      return newState;
    });
    toast.success(`Work Style Test sent to ${candidate.name}`);
  };

  const handleViewInterviewResults = (candidate: DemoCandidate) => {
    setSelectedCandidate(candidate);
    setInterviewResultsOpen(true);
  };

  const handleViewWorkstyleResults = (candidate: DemoCandidate) => {
    setSelectedCandidate(candidate);
    setWorkstyleResultsOpen(true);
  };

  const handleViewDetails = (candidate: DemoCandidate) => {
    setSelectedCandidate(candidate);
    setSelectedStageName(findStageName(candidate.id));
    setDetailsDrawerOpen(true);
  };

  const handlePrepareInterview = (candidate: DemoCandidate) => {
    setSelectedCandidate(candidate);
    setPrepareInterviewOpen(true);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    for (const stageId of Object.keys(candidatesByStage)) {
      const candidate = candidatesByStage[stageId].find((c) => c.id === active.id);
      if (candidate) {
        setActiveCandidate(candidate);
        break;
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveCandidate(null);

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    let sourceStageId: string | null = null;
    let sourceCandidate: DemoCandidate | null = null;
    
    for (const stageId of Object.keys(candidatesByStage)) {
      const candidate = candidatesByStage[stageId].find((c) => c.id === activeId);
      if (candidate) {
        sourceStageId = stageId;
        sourceCandidate = candidate;
        break;
      }
    }

    if (!sourceStageId || !sourceCandidate) return;

    let destStageId = overId;
    
    for (const stageId of Object.keys(candidatesByStage)) {
      if (candidatesByStage[stageId].some((c) => c.id === overId)) {
        destStageId = stageId;
        break;
      }
    }

    if (!initialStages.some((s) => s.id === destStageId)) return;
    if (sourceStageId === destStageId) return;

    setCandidatesByStage((prev) => {
      const newState = { ...prev };
      newState[sourceStageId!] = prev[sourceStageId!].filter((c) => c.id !== activeId);
      newState[destStageId] = [...prev[destStageId], sourceCandidate!];
      return newState;
    });
    
    const destStage = initialStages.find((s) => s.id === destStageId);
    toast.success(`Moved ${sourceCandidate.name} to ${destStage?.name}`);
  };

  return (
    <DocsComponentDemo 
      caption="Interactive pipeline board - drag candidates between stages, send interviews, and view results"
      fullWidth
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-3 overflow-x-auto pb-2">
          {initialStages.map((stage) => (
            <StageColumn
              key={stage.id}
              stage={stage}
              candidates={candidatesByStage[stage.id] || []}
              onSendInterview={handleSendInterview}
              onSendWorkstyle={handleSendWorkstyle}
              onViewInterviewResults={handleViewInterviewResults}
              onViewWorkstyleResults={handleViewWorkstyleResults}
              onViewDetails={handleViewDetails}
              onPrepareInterview={handlePrepareInterview}
            />
          ))}
        </div>

        <DragOverlay>
          {activeCandidate ? (
            <div className="w-72">
              <CandidateCardContent 
                candidate={activeCandidate} 
                stageKey={findStageKey(activeCandidate.id) || undefined}
                isOverlay 
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Dialogs & Sheets */}
      <InterviewResultsSheet
        open={interviewResultsOpen}
        onOpenChange={setInterviewResultsOpen}
        candidate={selectedCandidate}
      />
      <WorkStyleResultsSheet
        open={workstyleResultsOpen}
        onOpenChange={setWorkstyleResultsOpen}
        candidate={selectedCandidate}
      />
      <CandidateDetailsDrawer
        open={detailsDrawerOpen}
        onOpenChange={setDetailsDrawerOpen}
        candidate={selectedCandidate}
        currentStageName={selectedStageName}
      />
      <PrepareInterviewDrawer
        open={prepareInterviewOpen}
        onOpenChange={setPrepareInterviewOpen}
        candidate={selectedCandidate}
      />
    </DocsComponentDemo>
  );
}
